import { nanoid } from 'nanoid'
import { useFormContext } from 'react-hook-form'

import { Form } from '../../Form/index.js'
import { useStaticIntl } from '../../i18n/hooks/useStaticIntl.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { SortableItemList } from '../../SortableItemList/SortableItemList.js'
import { type ReactionItem, ReactionTypes } from '../types.js'
import { reactionTypeOptionsFactory } from '../utils/selectOptions.js'
import { slugify } from '../utils/utils.js'

interface Props {
  reactionItems: ReactionItem[]
  reactionError?: string
  setReactionItems: React.Dispatch<React.SetStateAction<ReactionItem[]>>
  setSelectedReactionId: (key: string) => void
}
export const ReactionsSettings = ({
  reactionItems,
  reactionError,
  setReactionItems,
  setSelectedReactionId,
}: Props) => {
  const { $t } = useI18n()
  const reactionOptions = useStaticIntl(reactionTypeOptionsFactory)
  const { watch } = useFormContext()
  const reactionType = watch('reactionType')

  const addReactionItem = () => {
    const newReactionItem: ReactionItem = {
      id: nanoid(),
      key: 'new-reaction',
      itemIcon: null,
      name: $t({
        defaultMessage: 'New reaction',
        id: 'Admin.CMS.Reactions.NewReaction',
      }),
      glyphId: '',
    }

    setReactionItems(prev => [...prev, newReactionItem])
  }

  const setItems = (items: ReactionItem[], renamedItemId?: string) => {
    if (renamedItemId) {
      const newItems = items.map(item => {
        if (item.id === renamedItemId) {
          return {
            ...item,
            key: slugify(item.name),
          }
        }
        return item
      })
      setReactionItems(newItems)
      return
    }

    setReactionItems(items)
  }

  return (
    <>
      <Form.Select
        label={$t({
          defaultMessage: 'Reaction type',
          id: 'Admin.CMS.ReactionType',
        })}
        name="reactionType"
        items={reactionOptions}
      />
      {reactionType !== ReactionTypes.none && (
        <Form.Toggle
          name="singleChoice"
          label={$t({
            defaultMessage: 'Single choice only',
            id: 'Admin.CMS.SingleChoiceOnly',
          })}
          helperText={$t({
            defaultMessage:
              'Limit users to select only one reaction from available reactions per post',
            id: 'Admin.CMS.SingleChoice.HelperText',
          })}
        />
      )}
      {reactionType === ReactionTypes.custom && (
        <SortableItemList
          items={reactionItems}
          setItems={setItems}
          onClick={id => setSelectedReactionId(id)}
          addItem={addReactionItem}
          buttonText={$t({
            defaultMessage: 'Add reaction',
            id: 'Admin.CMS.AddReaction',
          })}
          canDuplicate={false}
          error={reactionError}
        />
      )}
    </>
  )
}
